import { useQuery } from "@tanstack/react-query";
import { statusLabel } from "@wms/domain";
import type { PortalDashboard, PortalDocument } from "@wms/domain";
import { Button, Space, Statistic, Tag, Typography } from "antd";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { attentionApi } from "../api/attention";
import { formatDate, formatQty } from "../api/format";
import { portalApi } from "../api/portal";
import { PageHeader } from "../components/PageHeader";
import { unitTotalsText } from "../portal/portalText";
import { usePortalStore } from "../portal/store";
import { AttentionList } from "./AttentionList";
import { Desktop, type WidgetView } from "./Desktop";
import { DataTime, WidgetState } from "./widgetParts";

/** The widgets of the cabinet — the portal catalogue of the server (common/workspace.py). */
export const PORTAL_WIDGET_CODES = ["receipts", "orders", "requests", "attention", "stock", "recent"] as const;

const PREVIEWS: Record<string, string> = {
  receipts: "три числа — ожидаем на складе, принимаются, приняты",
  orders: "четыре числа — новые и подтверждённые, на сборке, готовы к отгрузке, отгружены",
  requests: "заявки, которые нужно уточнить, новые счета и документы к подписанию",
  stock: "доступный остаток отдельно по каждой единице и число товаров",
  attention: "номер заявки, счёта или АВР с причиной и ссылкой на него",
  recent: "пять последних поступлений и заказов с их состоянием",
};

// «Показать все N» of a group: the page of the cabinet with exactly those documents
const MORE: Record<string, string> = {
  requests: "/portal/requests",
  invoices: "/portal/finance?tab=invoices",
  acts: "/portal/finance?tab=acts",
};

/** UXW-26 / CW-18: the person of the cabinet is part of every key — another login never sees these numbers. */
function useClientKey(): string {
  return usePortalStore((s) => s.me?.user.id ?? "-");
}

/** CW-16: the blocks of the cabinet read one `/portal/dashboard` — one request, one refresh a minute. */
function usePortalDashboard() {
  const client = useClientKey();
  return useQuery({
    queryKey: ["portal", "dashboard", "desktop", client],
    queryFn: () => portalApi.get<PortalDashboard>("/dashboard"),
    refetchInterval: 60000,
  });
}

function DashboardTime() {
  const q = usePortalDashboard();
  return <DataTime at={q.dataUpdatedAt || null} fetching={q.isFetching} />;
}

function ReceiptsWidget() {
  const q = usePortalDashboard();
  const d = q.data;
  return (
    <WidgetState source={q}>
      {d && (
        <div className="wms-widget-stats">
          <Statistic title="Ожидаем на складе" value={d.receipts.expected} />
          <Statistic title="Принимаются" value={d.receipts.in_progress} />
          <Statistic title="Приняты" value={d.receipts.accepted} />
        </div>
      )}
    </WidgetState>
  );
}

function OrdersWidget() {
  const q = usePortalDashboard();
  const d = q.data;
  return (
    <WidgetState source={q}>
      {d && (
        <div className="wms-widget-stats">
          <Statistic title="Новые и подтверждённые" value={d.orders.new} />
          <Statistic title="На сборке" value={d.orders.picking} />
          <Statistic title="Готовы к отгрузке" value={d.orders.ready} />
          <Statistic title="Отгружены" value={d.orders.shipped} />
        </div>
      )}
    </WidgetState>
  );
}

function RequestsWidget() {
  const q = usePortalDashboard();
  const d = q.data;
  const clarify = d ? (d.requests.needs_clarification ?? 0) : 0;
  return (
    <WidgetState source={q}>
      {d && (
        <div className="wms-widget-stats">
          <Statistic title="Нужно уточнить" value={clarify} valueStyle={clarify ? { color: "var(--wms-op-writeoff-fg)" } : undefined} />
          <Statistic title="Новые счета" value={d.invoices.new} />
          <Statistic title="К подписанию" value={d.to_sign} />
        </div>
      )}
    </WidgetState>
  );
}

function StockWidget() {
  const q = usePortalDashboard();
  const d = q.data;
  return (
    <WidgetState source={q}>
      {d && (
        <>
          {d.stock.length === 0 && <Typography.Paragraph type="secondary">На складе пока нет вашего товара с подтверждённой принадлежностью.</Typography.Paragraph>}
          {/* ANA-01: one number per unit, never «всего товара» across pieces and kilograms */}
          <div className="wms-widget-stats">
            {d.stock.map((row) => (
              <Statistic key={row.unit} title={`${row.unit}, доступно`} value={formatQty(row.available)} />
            ))}
            <Statistic title="Товаров" value={d.stock_products} />
          </div>
        </>
      )}
    </WidgetState>
  );
}

/** CW31-B15 / UXW-19: «Подробнее» leads only a role that may open the list; the aggregates stay for everyone. */
function StockExtra() {
  const can = usePortalStore((s) => s.can);
  return (
    <>
      <DashboardTime />
      {can("portal.stock.read") ? (
        <Link to="/portal/stock" data-testid="portal-stock-more">
          Подробнее
        </Link>
      ) : null}
    </>
  );
}

function useAttention() {
  const client = useClientKey();
  return useQuery({
    queryKey: ["portal", "attention", "widget", client],
    queryFn: () => attentionApi.get("portal", { per_kind: 3, limit: 30 }),
    refetchInterval: 60000,
  });
}

function AttentionWidget() {
  const q = useAttention();
  return <WidgetState source={q}>{q.data && <AttentionList cabinet="portal" page={q.data} moreHref={(kind) => MORE[kind] ?? null} />}</WidgetState>;
}

function AttentionExtra() {
  const q = useAttention();
  return <DataTime at={q.data?.generated_at} fetching={q.isFetching} />;
}

type Operation = PortalDocument & { kind: "receipts" | "orders" };

function useRecent() {
  const client = useClientKey();
  const receipts = useQuery({
    queryKey: ["portal", "operations", "receipts", "desktop", client],
    queryFn: () => portalApi.get<PortalDocument[]>("/operations/receipts", { limit: 5 }),
    refetchInterval: 60000,
  });
  const orders = useQuery({
    queryKey: ["portal", "operations", "orders", "desktop", client],
    queryFn: () => portalApi.get<PortalDocument[]>("/operations/orders", { limit: 5 }),
    refetchInterval: 60000,
  });
  return { receipts, orders };
}

function RecentWidget() {
  const { receipts, orders } = useRecent();
  // one block for both lists: a failure of either is shown with «Повторить» for both
  const source = {
    data: receipts.data && orders.data ? true : undefined,
    isPending: receipts.isPending || orders.isPending,
    isError: receipts.isError || orders.isError,
    isFetching: receipts.isFetching || orders.isFetching,
    error: receipts.error ?? orders.error,
    refetch: () => Promise.all([receipts.refetch(), orders.refetch()]),
  };
  const rows: Operation[] = [
    ...(receipts.data ?? []).map((r) => ({ ...r, kind: "receipts" as const })),
    ...(orders.data ?? []).map((r) => ({ ...r, kind: "orders" as const })),
  ]
    .sort((a, b) => (b.date ?? "").localeCompare(a.date ?? "") || b.number.localeCompare(a.number))
    .slice(0, 5);
  return (
    <WidgetState source={source} empty={rows.length === 0} emptyText="Операций пока нет">
      <div className="wms-widget-list">
        {rows.map((r) => (
          <div key={`${r.kind}-${r.id}`} className="wms-widget-list-row" data-testid={`portal-recent-${r.number}`}>
            <span className="wms-grow">
              {r.kind === "receipts" ? "Поступление" : "Заказ"} <b>{r.number}</b> · {formatDate(r.date)}
              {r.totals_by_unit?.length ? <Typography.Text type="secondary"> · {unitTotalsText(r.totals_by_unit, "plan")}</Typography.Text> : null}
            </span>
            <Tag>{statusLabel(r.status)}</Tag>
            <Link to={`/portal/operations?kind=${r.kind}&open=${r.id}`} aria-label={`Открыть ${r.number}`}>
              Открыть
            </Link>
          </div>
        ))}
      </div>
    </WidgetState>
  );
}

function render(code: string): WidgetView {
  switch (code) {
    case "receipts":
      return { body: <ReceiptsWidget />, extra: <DashboardTime /> };
    case "orders":
      return { body: <OrdersWidget />, extra: <DashboardTime /> };
    case "requests":
      return { body: <RequestsWidget />, extra: <DashboardTime /> };
    case "stock":
      return { body: <StockWidget />, extra: <StockExtra /> };
    case "attention":
      return { body: <AttentionWidget />, extra: <AttentionExtra /> };
    case "recent":
      return { body: <RecentWidget /> };
    default:
      return { body: null };
  }
}

/**
 * ТЗ «Рабочие столы» 31.10.2026 (UXW-04/19, CW-10): the desktop of the client cabinet — «Рабочий стол» with the name
 * of the client, the buttons of HEAD by their rights and the blocks of HEAD as widgets. `legacy` is the page of HEAD,
 * shown when the own desktop cannot be read.
 */
export function PortalDesktop({ legacy }: { legacy: ReactNode }) {
  const me = usePortalStore((s) => s.me);
  const can = usePortalStore((s) => s.can);
  const d = usePortalDashboard().data;
  const header = (
    <>
      <PageHeader title="Рабочий стол" subtitle={me?.partner.name} />
      <Space wrap style={{ marginBottom: 16 }}>
        {can("portal.requests.create") && (
          <Link to="/portal/requests?new=inbound">
            <Button type="primary" data-testid="portal-new-inbound">
              Сообщить о поступлении
            </Button>
          </Link>
        )}
        {can("portal.requests.create") && (
          <Link to="/portal/requests?new=outbound">
            <Button type="primary" data-testid="portal-new-outbound">
              Заказать отгрузку
            </Button>
          </Link>
        )}
        {can("portal.stock.read") && (
          <Link to="/portal/stock">
            <Button>Посмотреть остатки</Button>
          </Link>
        )}
        {can("portal.finance.read") && (
          <Link to="/portal/finance?tab=acts">
            <Button>Документы к подписанию{d?.to_sign ? ` (${d.to_sign})` : ""}</Button>
          </Link>
        )}
      </Space>
    </>
  );
  return <Desktop cabinet="portal" codes={PORTAL_WIDGET_CODES} render={render} previews={PREVIEWS} header={header} fallback={legacy} />;
}
